'use client';

import { Control } from 'react-hook-form';
import * as z from 'zod';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useTranslations } from '@/hooks/useTranslations';
import { CreateProductSchema } from '@/schemas';

type FormValues = z.infer<typeof CreateProductSchema>;

interface ProductFormFieldsProps {
  control: Control<FormValues>;
  disabled?: boolean;
}

export function ProductFormFields({
  control,
  disabled = false,
}: ProductFormFieldsProps) {
  const t = useTranslations('CreateProductPopup');

  return (
    <>
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t('name')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={disabled}
                placeholder={t('namePlaceholder')}
                type="text"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="price"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t('price')}</FormLabel>
            <FormControl>
              <Input
                disabled={disabled}
                placeholder={t('pricePlaceholder')}
                type="number"
                step="0.01"
                value={
                  field.value === 0 ||
                  field.value === null ||
                  field.value === undefined
                    ? ''
                    : field.value
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || value === '.') {
                    field.onChange(0);
                    return;
                  }
                  const numValue = parseFloat(value);
                  field.onChange(isNaN(numValue) ? 0 : numValue);
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="sku"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t('sku')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                disabled={disabled}
                placeholder={t('skuPlaceholder')}
                type="text"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="unit"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t('unit')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                disabled={disabled}
                placeholder={t('unitPlaceholder')}
                type="text"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t('descriptionLabel')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                disabled={disabled}
                placeholder={t('descriptionPlaceholder')}
                type="text"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
